/**
 * The Unternehmen board, looked up by company name — shared by qualification
 * (website) and the e-mail intake (LandVest) so the two cannot match a company
 * differently.
 */

import type { MondayClient } from "./monday-client";
import { pagedItems } from "./paged-items";
import { normaliseCompanyName } from "./company-name.mjs";
export { normaliseCompanyName } from "./company-name.mjs";

/**
 * The id of the Unternehmen whose name matches exactly once normalised, or null.
 *
 * Monday's name filter is a CONTAINS (case-insensitive, verified live), so it
 * can only narrow — the exact comparison is `normaliseCompanyName`. Paginated
 * because a common substring like „Bau" pushes the real match off page one, and
 * reporting that as "no such account" writes a factually false note. The name
 * goes in through `JSON.stringify`: GraphQL string literals take JSON's escapes,
 * and a hand-rolled quote escape let a backslash in „Wald\Feld GmbH" break the
 * whole query.
 */
export async function findCompanyByName(
  client: MondayClient,
  companiesBoardId: string,
  name: string
): Promise<string | null> {
  const matches = await findCompaniesByName(client, companiesBoardId, name);
  // Preserve legacy qualification semantics; admission uses all matches below.
  return matches[0]?.id ?? null;
}

/** All exact matches: ownership admission must hold ambiguity rather than select the first. */
export async function findCompaniesByName(
  client: MondayClient,
  companiesBoardId: string,
  name: string,
  requireCompleteCursor = false
): Promise<{ id: string; name: string }[]> {
  const wanted = normaliseCompanyName(name);
  if (!wanted) return [];
  // Full normalized names are not a safe server substring: doubled whitespace or
  // decomposed Unicode can hide an exact normalized match. Ownership needs a full scan.
  const rules = requireCompleteCursor
    ? ""
    : `, query_params:{rules:[{column_id:"name", compare_value:${JSON.stringify([wanted])}, operator:contains_text}]}`;
  const items = await pagedItems<{ id: string; name: string }>(
    client,
    companiesBoardId,
    rules,
    "id name",
    requireCompleteCursor
  );
  return items.filter((i) => normaliseCompanyName(i.name) === wanted);
}
